import * as React from "react";
import {
    Modal, Box, Typography, IconButton, Grid, TextField, Select, MenuItem,
    Divider, Stack, InputAdornment,
    Skeleton,
    Button
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CtaButton from "../buttonWithArrow";
import { useDispatch, useSelector } from '../../redux/store';
import { styled } from "@mui/material/styles";
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import CheckIcon from '@mui/icons-material/Check';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import LockIcon from "@mui/icons-material/Lock";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DynamicPillButton from "../buttonWithArrow/whatsappButton";
import { lockScroll, unlockScroll } from "../../utils/scrollLock";
import { FormControl, FormHelperText } from "@mui/material";
import { getCommercialListQuery } from "../../redux/slices/investments";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CurrencySimple, useResponsiveValue } from "../../utils/helper";
import { openWhatsAppChatForCommercialBroker } from "../../utils/onWhatsApp";

// ---------- helpers ----------
const CURRENCY = {
    USD: { sign: "$", rate: 1, type: "string" },
    AED: { sign: "images/bannerImage/1758795455186.svg", rate: 3.67, type: "image" },
    // EUR: { sign: "€", rate: 0.92 },
};


// pill look
const pillInputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 999 },
};

// stat pill
const statSx = {
    p: 2,
    borderRadius: 2.5,
    border: "1px solid #E6E8EC",
    bgcolor: "#fff",
    textAlign: "center",
};

const TEAL_BOTTOM = "#0D8196";

// 1. Connector style (line between steps)
const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 18,
        left: "calc(-50% + 20px)",
        right: "calc(50% + 20px)",
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
        borderColor: "linear-gradient(90deg,#0D8196,#8AD9D9)",
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
        borderColor: "#0D8196",
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: "#E5E7EB",
        borderTopWidth: 2,
        borderRadius: 1,
    },
}));

const TILE_W = 130;
const TILE_H = 120;

/* --- Row-level skeleton (use while fetching) --- */
function BusinessRowSkeleton({ count = 5 }) {
    return (
        <Box sx={{ display: "flex", gap: 2, overflowX: "auto", py: 1, px: 1, mt: 2 }}>
            {Array.from({ length: count }).map((_, i) => (
                <Box key={i} sx={{ flex: "0 0 auto" }}>
                    <Skeleton variant="rounded" width={TILE_W} height={TILE_H} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="text" width={TILE_W * 0.7} sx={{ mt: 1 }} />
                </Box>
            ))}
        </Box>
    );
}

// 2. Step icon component
function QontoStepIcon(props) {
    const { activeStep, index } = props; // index is 1-based

    const isActive = activeStep === index;
    const isCompleted = index < activeStep;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {isCompleted ? (
                <CheckIcon
                    sx={{
                        color: "#fff",
                        background: "linear-gradient(135deg, #0D8196, #8AD9D9)",
                        borderRadius: "50%",
                        width: 40,
                        height: 40,
                        padding: "6px",
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        border: `2px solid ${isActive ? "none" : "#D1D5DB"}`,
                        background: isActive ? "linear-gradient(135deg, #0D8196, #8AD9D9)" : "#fff",
                        color: isActive ? "#fff" : "#9CA3AF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 18,
                    }}
                >
                    {index}
                </Box>
            )}
        </Box>
    );
}

const steps = [
    'Info',
    'Choose Business',
    'Finalize Listing',
    'Lock Investment',
];

const jetTileSx = (active) => ({
    objectFit: "contain", borderRadius: 3, "&:hover": { border: "2px solid #0d8196" },
    border: active ? "2px solid #0d8196" : "none"
});

const jetLabelSx = (active) => ({
    textAlign: "center", mt: 0.75, fontWeight: 700, fontSize: 13,
    color: active ? TEAL_BOTTOM : "#111827",
});

const businesses = [
    { name: "Private Jet", image: "images/bannerImage/1758869048988.svg", locked: true },
    { name: "Limousine", image: "images/bannerImage/1758869048988.svg", locked: true },
    { name: "Travel & Tourism", image: "images/bannerImage/1758869048988.svg", locked: true },
    { name: "Car Rental", image: "images/bannerImage/1758869048988.svg", locked: false },
    { name: "Commercial Broker", image: "images/bannerImage/1758869048988.svg", locked: false },
    { name: "Ecommerce", image: "images/bannerImage/1758869048988.svg", locked: false },
];

const profits = [
    { label: "Your Profit after 6 months", value: "14,843" },
    { label: "Your Profit after 1 Year", value: "29,843" },
    { label: "Your Profit after 5 Year", value: "200,564" },
];

// ---------- main component ----------
export default function InvestmentSimulatorFlow({
    open,
    onClose,
    onInvest, // (payload) => void
    activeStep,
    setActiveStep, categoryList
}) {
    const dispatch = useDispatch();
    const [step, setStep] = React.useState(1);
    const [amount, setAmount] = React.useState(null);
    const [investAmount, setInvestAmount] = React.useState(0);
    const [currency, setCurrency] = React.useState("AED");
    const [businessVal, setBusinessVal] = React.useState(null);
    const [productData, setProductData] = React.useState(null);
    const [selected, setSelected] = React.useState(null);
    const [shares, setShares] = React.useState(1);
    const [profits, setProfits] = React.useState([]);
    const [loader, setLoader] = React.useState(true);
    const [productList, setProductList] = React.useState([]);
    const [activeId, setActiveId] = React.useState(null);
    const { commercialListQueryRes } = useSelector(
        state => state.investment
    );
    const [errors, setErrors] = React.useState(null);
    const parsedAmount = Number(String(amount).replace(/,/g, "")) || 0;
    const [totalShares, setTotalShares] = React.useState(100);

    React.useEffect(() => {
        if (!productData?.price || !investAmount) {
            setProfits([]);
            return;
        }

        const amount = parseFloat(investAmount) || 0;

        // Example logic (customize multipliers as needed)
        const profit6M = amount * 0.10;   // 10% after 6 months
        const profit1Y = amount * 0.20;   // 20% after 1 year
        const profit5Y = amount * 1.20;   // 120% after 5 years

        setProfits([
            { label: "Your Profit after 6 months (Apr.)", value: profit6M.toFixed(0) },
            { label: "Your Profit after 1 Year (Apr.)", value: profit1Y.toFixed(0) },
            { label: "Your Profit after 5 Year (Apr.)", value: profit5Y.toFixed(0) },
        ]);
    }, [investAmount, productData?.price]);


    React.useEffect(() => {
        if (!productData?.price) return;
        const amount = parseFloat(investAmount || 0);
        const calcShares = amount / productData.price;
        if (!isNaN(calcShares)) setShares(Math.floor(calcShares));
    }, [investAmount, productData?.price]);


    React.useEffect(() => {
        if (open) lockScroll();
        else unlockScroll();

        return () => unlockScroll(); // cleanup if component unmounts
    }, [open]);

    // when categoryList updates (e.g., after fetch), rebind selected object by _id
    React.useEffect(() => {
        if (categoryList && categoryList?.length > 0) {
            setBusinessVal(categoryList[0])
        }
    }, [categoryList]);

    const resetAll = () => {
        setStep(1);
        setAmount(null);
        setInvestAmount(0);
        setCurrency("AED");
        setBusinessVal(null);
        setProductData(null);
        setSelected(null);
        setShares(1);
        setProductList([]);
        setActiveId(null);
        setErrors(null);
    };

    const closeAll = () => {
        setActiveStep(1);
        onClose();
        setTimeout(resetAll, 300);
    };

    const ONLY_DIGITS = (s) => s.replace(/\D/g, "");
    const MAX_DIGITS = 12; // adjust if you want a cap

    const responsiveFontSize = useResponsiveValue({ xs: 22, sm: 22, lg: 22 });

    function stepperResponse(n) {
        switch (n) {
            case 1:
                return (
                    <Grid container spacing={2} mt={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Amount</Typography>
                            <TextField
                                fullWidth
                                name="amount"
                                value={amount}
                                placeholder="Enter amount"
                                sx={pillInputSx}
                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: MAX_DIGITS }}
                                onKeyDown={(e) => {
                                    if (e.isComposing) return; // allow IME
                                    const control = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
                                    const combo = (e.ctrlKey || e.metaKey) && ["a", "c", "v", "x", "z", "y"].includes(e.key.toLowerCase());
                                    if (control.includes(e.key) || combo) return;
                                    if (!/^\d$/.test(e.key)) e.preventDefault();
                                }}
                                onPaste={(e) => {
                                    const text = e.clipboardData.getData("text");
                                    if (/[^0-9]/.test(text)) e.preventDefault();
                                }}
                                onDrop={(e) => e.preventDefault()}
                                onChange={(e) => {
                                    setAmount(ONLY_DIGITS(e.target.value));
                                    setInvestAmount(ONLY_DIGITS(e.target.value));
                                }}
                                error={Boolean(errors?.amount)}
                                helperText={errors?.amount ? "This field is required" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Currency</Typography>
                            <Select
                                fullWidth
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                sx={{ ...pillInputSx, "& fieldset": { borderRadius: 999 } }}
                                renderValue={(v) => (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {CURRENCY[v].type === "string" ? (
                                            <>{CURRENCY[v].sign}</>
                                        ) : (
                                            <img
                                                src={`${process.env.IMAGE_BASE_URL}/${String(CURRENCY[v].sign)}`}
                                                alt="currency"
                                                style={{ width: "1em", height: "1em", objectFit: "contain" }}
                                            />
                                        )}
                                        {v}
                                    </Box>
                                )}
                            >
                                {Object.keys(CURRENCY).map((c) => (
                                    <MenuItem key={c} value={c}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            {CURRENCY[c].type === "string" ? (
                                                <>{CURRENCY[c].sign}</>
                                            ) : (
                                                <img
                                                    src={`${process.env.IMAGE_BASE_URL}/${String(CURRENCY[c].sign)}`}
                                                    alt="currency"
                                                    style={{ width: "1em", height: "1em", objectFit: "contain" }}
                                                />
                                            )}
                                            {c}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                                Select the business you are interested in
                            </Typography>

                            <FormControl
                                fullWidth
                                error={Boolean(errors?.businessVal)}
                                sx={{ ...pillInputSx, "& fieldset": { borderRadius: 999 } }}
                            >
                                <Select
                                    displayEmpty
                                    value={businessVal} // allow empty before selection
                                    onChange={(e) => {
                                        setBusinessVal(e.target.value);
                                        setProductData(null);
                                    }}
                                    renderValue={(v) =>
                                        v && typeof v === "object"
                                            ? v.name
                                            : <Box sx={{ color: "#9CA3AF" }}>Select a business</Box>
                                    }
                                >
                                    <MenuItem value="" disabled>Select a business</MenuItem>
                                    {categoryList?.map((c) => (
                                        <MenuItem key={c._id} value={c /* store object */}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>{c.name}</Box>
                                        </MenuItem>
                                    ))}
                                </Select>

                                <FormHelperText>
                                    {errors?.businessVal ? "This field is required" : " "}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                );
            case 2:
                return (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                overflowX: "auto",
                                py: 1,
                                px: 1,
                                mt: 2,
                                "&::-webkit-scrollbar": {
                                    height: 6,
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "transparent",
                                    borderRadius: 3,
                                },
                            }}
                        >
                            {
                                loader ? <BusinessRowSkeleton count={4} /> :
                                    productList?.map((b) => {
                                        const isLocked = !!b?.locked;
                                        const isActive = activeId === b?._id;   // or any unique key

                                        return (
                                            <Box key={b?._id} sx={{ flex: "0 0 auto" }}>
                                                <Box
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => {
                                                        if (isLocked) return;
                                                        setActiveId(b._id);
                                                        setProductData(b); // your existing callback
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (!isLocked && (e.key === "Enter" || e.key === " ")) {
                                                            setActiveId(b._id);
                                                            setProductData(b);
                                                        }
                                                    }}
                                                    sx={{
                                                        width: 130,
                                                        height: 120,
                                                        borderRadius: 4,
                                                        overflow: "hidden",
                                                        position: "relative",
                                                        cursor: isLocked ? "not-allowed" : "pointer",
                                                        filter: isLocked ? "grayscale(80%) brightness(0.6)" : "none",
                                                        transition: "transform .2s, box-shadow .2s",
                                                        // active look: gradient ring + lift
                                                        border: isActive ? "3px solid transparent" : businessVal?.name === "Ecommerce" ? "3px solid transparent" : "3px solid #e8e8e8",
                                                        background: isActive
                                                            ? "linear-gradient(#fff,#fff) padding-box, linear-gradient(180deg,#75C4C5 0%, #0D8196 100%) border-box"
                                                            : "#fff",
                                                        // boxShadow: isActive ? "0 6px 16px rgba(13,129,150,.25)" : "0 2px 8px rgba(0,0,0,.06)",
                                                        transform: isActive ? "scale(1.02)" : "none",
                                                        "&:hover": { transform: isLocked ? "none" : "scale(1.05)" },
                                                    }}
                                                >
                                                    <img
                                                        src={`${process.env.IMAGE_BASE_URL}/${b?.image}`}
                                                        alt={b?.name}
                                                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                    />

                                                    {/* lock overlay */}
                                                    {isLocked && (
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                inset: 0,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                bgcolor: "rgba(0,0,0,0.4)",
                                                            }}
                                                        >
                                                            <LockIcon sx={{ color: "#fff", fontSize: 36 }} />
                                                        </Box>
                                                    )}

                                                    {/* active check badge */}
                                                    {/* {isActive && !isLocked && (
                                                    <CheckCircleIcon
                                                        sx={{
                                                            position: "absolute",
                                                            top: 6,
                                                            right: 6,
                                                            color: "#0D8196",
                                                            bgcolor: "#fff",
                                                            borderRadius: "50%",
                                                        }}
                                                    />
                                                )} */}
                                                </Box>

                                                <Typography
                                                    sx={{
                                                        textAlign: "center",
                                                        mt: 1,
                                                        fontWeight: isActive ? 800 : 600,
                                                        fontSize: 13,
                                                        width: 120,
                                                        color: isActive ? "#0D8196" : "#111827",
                                                    }}
                                                >
                                                    {b?.name ?? b?.title}
                                                </Typography>
                                            </Box>
                                        );
                                    })
                            }

                        </Box>
                        {
                            errors?.productData &&
                            <Box sx={{ color: "red", fontSize: "12px" }}>{errors?.productData}</Box>
                        }
                    </>
                );
            case 3:
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 3,
                            mt: 2,
                        }}
                    >
                        {/* Left image */}
                        <Box
                            component="img"
                            src={`${process.env.IMAGE_BASE_URL}/${productData?.image}`}
                            alt="Travel & Tourism"
                            sx={{
                                width: 250,
                                height: 250,
                                borderRadius: 2,
                                objectFit: "contain",
                            }}
                        />

                        {/* Right content */}
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ color: "#0D8196", fontWeight: 600, mb: 0.5 }}>
                                Congratulations!
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                You choose <b>{businessVal?.name}</b> for investment.
                            </Typography>

                            {/* Amount */}
                            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                Total Amount you want to invest
                            </Typography>
                            <TextField
                                fullWidth
                                name="investAmount"
                                value={investAmount}
                                placeholder="Enter amount"
                                sx={pillInputSx}
                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: MAX_DIGITS }}
                                onKeyDown={(e) => {
                                    if (e.isComposing) return; // allow IME
                                    const control = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
                                    const combo = (e.ctrlKey || e.metaKey) && ["a", "c", "v", "x", "z", "y"].includes(e.key.toLowerCase());
                                    if (control.includes(e.key) || combo) return;
                                    if (!/^\d$/.test(e.key)) e.preventDefault();
                                }}
                                renderValue={(v) => (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        {CurrencySimple(null, null, responsiveSelectFont, CURRENCY[currency]?.type,
                                            CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}
                                        {v}
                                    </Box>
                                )}
                                onPaste={(e) => {
                                    const text = e.clipboardData.getData("text");
                                    if (/[^0-9]/.test(text)) e.preventDefault();
                                }}
                                onDrop={(e) => e.preventDefault()}
                                onChange={(e) => setInvestAmount(ONLY_DIGITS(e.target.value))}
                                error={Boolean(errors?.investAmount)}
                                helperText={errors?.investAmount ? "This field is required" : ""}
                            />

                            {/* Shares row */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                {/* Total Shares */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Total Shares
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        value={totalShares}
                                        onChange={(e) => setTotalShares(e.target.value)}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 999,
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Shares you will get */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Shares you will get
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            border: "1px solid #E6E8EC",
                                            borderRadius: 999,
                                            px: 2,
                                            py: 1,
                                            minWidth: 120,
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const newShares = Math.max(1, shares - 1);
                                                setShares(newShares);
                                                setInvestAmount(String(newShares * productData?.price));
                                            }}
                                        // onClick={() => setShares(Math.max(1, shares - 1))}
                                        >
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        <Typography sx={{ fontWeight: 600, color: "#0D8196" }}>
                                            {shares}
                                        </Typography>
                                        <IconButton size="small"
                                            // onClick={() => setShares(shares + 1)
                                            onClick={() => {
                                                const newShares = shares + 1;
                                                setShares(newShares);
                                                setInvestAmount(String(newShares * productData?.price));
                                            }}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box >
                );
            case 4:
                return (
                    <Box sx={{ textAlign: "center", mt: 4 }}>
                        {/* Profit cards */}
                        <Grid container spacing={2} justifyContent="center">
                            {profits.map((p) => (
                                <Grid item xs={12} sm={4} key={p.label}>
                                    <Box
                                        sx={{
                                            border: "1px solid #E6E8EC",
                                            borderRadius: 2,
                                            p: 2,
                                            bgcolor: "#fff",
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                            {p.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 22,
                                                // background: "linear-gradient(90deg, #75C4C5, #0D8196)",
                                                // WebkitBackgroundClip: "text",
                                                // WebkitTextFillColor: "transparent",
                                                color: "#8dd2c9",
                                                display: 'flex',
                                                justifyContent: 'center',
                                                gap: "2px"
                                            }}
                                        >
                                            {CurrencySimple(null, "#8dd2c9", responsiveFontSize, CURRENCY[currency]?.type,
                                                CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}{p.value}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Buttons */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 3 }}>
                            <CtaButton label="Back" iconPosition="start" onClick={() => setActiveStep(n - 1)} variant="secondary" sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorLeft.svg" />
                            <Box sx={{ display: "flex", gap: "8px" }}>
                                <CtaButton label=" Book a Meeting" sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorRight.svg" onClick={() => window.open(process.env.BOOK_A_CALL, "_blank")} />

                                <DynamicPillButton
                                    label="Send a message"
                                    icon="/img/header/whatsapp.svg"
                                    iconPosition="end"
                                    bg="#25D366"
                                    onClick={() => {
                                        openWhatsAppChatForCommercialBroker(productData?.name ?? productData?.title, businessVal?.name, investAmount, shares, currency)
                                    }}
                                    size="md"
                                    sx={{ height: "100% !important", minHeight: "50px", fontSize: { xs: "14px", sm: "auto" } }}
                                />
                            </Box>
                        </Box>
                    </Box>
                );
            case 5:
                return (
                    <Grid container spacing={2}>
                        {/* Full Name */}
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                Full Name
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter your full name"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        </Grid>

                        {/* Contact Number */}
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                Contact Number
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter your contact"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        </Grid>

                        {/* Email */}
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                Email
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter your email"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        </Grid>

                        {/* Address */}
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                Address
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter your address"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        </Grid>

                        {/* Business */}
                        <Grid item xs={12}>
                            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                Please add the business that you want to sell
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter the business here"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                )
            case 6:
                return (
                    <Box
                        component="img"
                        src={`/img/investments/commercial/objects.svg`}
                        alt="submitted"
                        sx={{
                            width: "auto",
                            height: "auto",
                            mx: "auto",
                            display: "flex",
                            justifyContent: "center"
                        }}
                    />
                )
            default:
                return null;
        }
    }

    const handleStepTwo = () => {
        const nextErrors = {};

        if (activeStep === 1) {
            const amt = Number(String(amount).replace(/,/g, ""));
            if (!amount || Number.isNaN(amt) || amt <= 0) nextErrors.amount = "Enter a valid amount";
            if (!currency) nextErrors.currency = "Select a currency";
            if (!businessVal) nextErrors.businessVal = "Select a business";

            // set & stop if errors
            if (Object.keys(nextErrors).length) {
                setErrors(nextErrors);
                return;
            }
            setErrors({});
            setActiveStep(activeStep + 1);
            const obj = {
                "amount": amount,
                "currency": currency,
                "serviceId": businessVal?._id
            }
            setLoader(true);
            dispatch(getCommercialListQuery(obj))
        } else if (activeStep === 2) {
            if (!productData) nextErrors.productData = "Select a product";

            // set & stop if errors
            if (Object.keys(nextErrors).length) {
                setErrors(nextErrors);
                return;
            }
            setErrors({});
            setActiveStep(activeStep + 1);
        }
        else {
            setActiveStep(activeStep + 1);
        }
    };

    React.useEffect(() => {
        if (commercialListQueryRes) {
            setLoader(false);
            if (commercialListQueryRes?.status === 1) {
                setProductList(commercialListQueryRes?.responseData);
            } else {
                setProductList([]);
            }
        }
    }, [commercialListQueryRes])

    return (
        <Modal open={open} onClose={closeAll}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
            <Box sx={{ position: "relative", width: activeStep === 6 ? 500 : 820, maxWidth: "95vw", bgcolor: "#fff", borderRadius: 4, p: 3, pb: activeStep === 6 ? 0 : 3 }}>
                {/* close */}
                <IconButton onClick={closeAll} sx={{ position: "absolute", top: 8, right: 8 }}>
                    <CloseRoundedIcon />
                </IconButton>

                {/* ---------------------- STEP HEADER ---------------------- */}
                {step !== 3 ?
                    activeStep === 6 ?
                        <>
                            <Typography className="jura" sx={{ fontWeight: 800, fontSize: 24, textAlign: "center" }}>
                                Your Request is Submitted
                            </Typography>
                            <Typography sx={{ color: "#6b7280", textAlign: "center", mt: 1, mb: 2 }}>
                                RSL Team will contact you soon, Thanks for your patience!
                            </Typography>
                        </> :
                        <>
                            <Typography className="jura" sx={{ fontWeight: 800, fontSize: 24, textAlign: "center" }}>
                                Investment calculator simulator
                            </Typography>
                            {
                                [1, 2, 3, 4].includes(activeStep) ? <Typography sx={{ color: "#6b7280", textAlign: "center", mt: 1, mb: 2 }}>
                                    Enter your amount to see tailored options where you can invest and grow with RSL.
                                </Typography> :
                                    <Typography sx={{ color: "#6b7280", textAlign: "center", mt: 1, mb: 2 }}>
                                        Enter the detail below to sell your business with RSL
                                    </Typography>
                            }
                        </>
                    : (
                        <Typography className="jura" sx={{ fontWeight: 800, fontSize: 28, textAlign: "center", mb: 1 }}>
                            {selected?.title}
                        </Typography>
                    )}

                {/* ---------------------- STEP 1 & 2 ---------------------- */}
                {step !== 3 && (
                    <Box sx={{ p: 2 }}>

                        {
                            [1, 2, 3, 4].includes(activeStep) &&
                            <Box sx={{ width: '100%' }}>
                                <Stepper
                                    alternativeLabel
                                    activeStep={activeStep}
                                    connector={<QontoConnector />}
                                    sx={{
                                        px: 0, // remove default horizontal padding                                    
                                        '& .MuiStep-root:first-of-type': {
                                            alignItems: 'flex-start',
                                            flex: 1,
                                        },
                                        '& .MuiStep-root:last-of-type': {
                                            alignItems: 'flex-end',
                                            flex: 1,
                                        },
                                        "& .MuiStepLabel-label": {
                                            fontWeight: 600,
                                            color: "#9CA3AF", // default gray
                                        },
                                        "& .Mui-active .MuiStepLabel-label": {
                                            background: "linear-gradient(180deg, #75C4C5, #0D8196)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            fontWeight: 700,
                                        },
                                        "& .Mui-completed .MuiStepLabel-label": {
                                            background: "linear-gradient(180deg, #75C4C5, #0D8196)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            fontWeight: 700,
                                        },
                                    }}
                                >
                                    {steps.map((label, index) => (
                                        <Step key={label}>
                                            <StepLabel
                                                StepIconComponent={() => (
                                                    <QontoStepIcon activeStep={activeStep} index={index + 1} />
                                                )}
                                            >
                                                {label}
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>
                        }

                        {stepperResponse(activeStep)}

                        <Box className="cta" mt={activeStep === 6 ? 0 : 3} display="flex" justifyContent={activeStep === 5 ? "end" : activeStep > 1 ? "space-between" : "end"}>
                            {
                                activeStep > 1 && activeStep < 4 && <CtaButton label="Back" iconPosition="start" onClick={() => setActiveStep(activeStep - 1)} variant="secondary" sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorLeft.svg" />
                            }
                            {
                                activeStep < 4 &&
                                <CtaButton label="Next" onClick={() => handleStepTwo()} sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorRight.svg" />
                            }
                            {
                                activeStep === 4 &&
                                <Typography
                                    sx={{
                                        mt: 3,
                                        fontSize: 13,
                                        color: "#6B7280",
                                    }}
                                >
                                    Given Values are approximate, subject to change based on market factors*
                                </Typography>
                            }
                            {
                                activeStep === 5 &&
                                <CtaButton label="Submit Request" onClick={() => setActiveStep(6)} sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorRight.svg" />
                            }
                        </Box>

                    </Box>
                )}
            </Box>
        </Modal >
    );
}

// ---------- tiny subcomponents ----------
// Replace your Chiplet with this
function Chiplet({ label }) {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                px: 2.5,
                py: 1,
                borderRadius: 2.5, // ≈10px
                // gradient outline over white background
                border: "2px solid transparent",
                background:
                    "linear-gradient(#fff,#fff) padding-box, linear-gradient(180deg,#8AD9D9 0%, #0D8196 100%) border-box",
                color: "#0D8196",
                fontWeight: 600,
                fontSize: { xs: 14, sm: 16 }, // adjust to taste
                lineHeight: 1.2,
            }}
        >
            {label}
        </Box>
    );
}


function Stat({ title, value, type, sign, teal = false }) {
    return (
        <Box sx={statSx}>
            <Typography sx={{ color: "#6b7280", fontWeight: 700, mb: 0.5, fontSize: { xs: 12, sm: 16 } }}>{title}</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 28 }, color: teal ? "#0D8196" : "#111827" }}>
                {type && sign && (
                    type === "string" ? (
                        <>{sign}</>
                    ) : (
                        <img
                            src={`${process.env.IMAGE_BASE_URL}/${String(sign)}`}
                            alt="currency"
                            style={{ width: "1em", height: "1em", objectFit: "contain" }}
                        />
                    )
                )}
                {value}
            </Typography>
        </Box>
    );
}
